import type { LandingEditorElementDefinition, LandingEditorFieldKind } from './landingEditorRegistry.js';

const urlKeys = new Set(['href', 'linkedinUrl', 'mapEmbedUrl', 'phoneNumber']);
const imageKeys = new Set(['image', 'logo', 'brandLogo']);
const videoKeys = new Set(['video', 'videoUrl', 'heroVideo']);
const technicalKeys = new Set(['locale', 'profession', 'freshCounterSubtype']);

const kindFor = (key: string, value: string): LandingEditorFieldKind => {
  if (urlKeys.has(key) || /^(https?:|mailto:|tel:|\/|#)/u.test(value)) return 'url';
  if (videoKeys.has(key) || /\.(?:mp4|webm|mov)(?:$|\?)/iu.test(value)) return 'video';
  if (imageKeys.has(key) || /\.(?:avif|gif|jpe?g|png|svg|webp)(?:$|\?)/iu.test(value)) return 'image';
  if (key === 'iconClass') return 'icon';
  return 'text';
};

const collectStrings = (
  value: unknown,
  path: string[] = [],
): Array<{ path: string; key: string; value: string }> => {
  if (Array.isArray(value)) {
    return value.flatMap((entry, index) => collectStrings(entry, [...path, String(index)]));
  }
  if (!value || typeof value !== 'object') return [];

  return Object.entries(value as Record<string, unknown>).flatMap(([key, child]) => {
    if (technicalKeys.has(key)) return [];
    const childPath = [...path, key];
    if (typeof child === 'string') {
      return [{ path: childPath.join('.'), key, value: child }];
    }
    return collectStrings(child, childPath);
  });
};

/**
 * Adds a safe inspector fallback for fields that do not yet have a hand-authored WYSIWYG mapping.
 * This guarantees that every persisted string remains editable while preserving the richer,
 * element-specific definitions used for direct canvas editing.
 */
export const appendMissingContentDefinitions = (
  definitions: LandingEditorElementDefinition[],
  content: unknown,
  options: Pick<LandingEditorElementDefinition, 'source' | 'staticPageKey'> = {},
) => {
  const registeredPaths = new Set(
    definitions.map(definition => definition.path).filter((path): path is string => Boolean(path)),
  );

  for (const field of collectStrings(content)) {
    if (registeredPaths.has(field.path)) continue;
    definitions.push({
      id: `content.${field.path}`,
      label: field.path,
      kind: kindFor(field.key, field.value),
      path: field.path,
      parentId: 'page.root',
      multiline: field.value.length > 80 || field.value.includes('\n'),
      typographyMode: 'none',
      ...options,
    });
  }

  return definitions;
};
